import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { frontendURL, isPlatformHost } from 'config';

/**
 * Gate for every dashboard route: right host, then signed in.
 */
export default function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // The same bundle is served for every hostname, so without this the whole
  // dashboard renders on each brand's custom domain as well as the platform's.
  // That origin is customer-controlled and shared with their storefront, which
  // carries brand-authored page-builder content — an admin surface holding a
  // platform-wide token has no business living there. Send them to the real
  // dashboard instead of half-rendering one here.
  if (!isPlatformHost()) {
    const target = `${frontendURL || 'https://techproone.com'}${location.pathname}${location.search}`;
    if (typeof window !== 'undefined') window.location.replace(target);
    return null;
  }

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress color='primary' />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Keep the query string. The OAuth callback redirects back here with
    // `?auth=failed` / `?auth=deactivated` on a failed social sign-in, and
    // dropping it left the user staring at the landing page with no idea
    // why they were bounced. PlatformLanding reads it and explains.
    return (
      <Navigate
        to={{ pathname: '/', search: location.search }}
        state={{ openAuth: true, from: location.pathname }}
        replace
      />
    );
  }

  return children;
}
